// Vendor closures (migration 032): days a vendor doesn't work, like Chinese New Year. A lead time counts the days a
// vendor works, so the closed days inside an order's lead time are added to it: an order placed on day o with a
// 30-day lead time lands once 30 open days have passed (it's on hold while they're closed, and one placed during a
// closure starts when they're back). Order-by is the latest day an order still lands by the day it's needed, so it
// moves earlier by the closure; an order whose lead time misses the closure is unchanged. Shared by the plan
// (shared/planning.ts: the suggestion, planned purchases, the MRP's orders, auto mode, the Make plan) and the screens.
//
// Which closures a vendor has: its country's (vendor.country), unless it has its own dates for one (a row of its own
// with `replaces_id`; the same day twice = it doesn't close), plus any of its own. Day numbers are whole days
// (shared/planning.ts dayNum()), relative to the plan's today when it makes them.
import type { Vendor, VendorClosure } from "./db.gen.ts";

export type ClosureRow = Pick<VendorClosure, "id" | "label" | "closed_from" | "back_on" | "country" | "vendor_id" | "replaces_id">;

export type Closure = {
  id: string;
  label: string;
  /** The first day closed and the first day back (ISO). */
  closedFrom: string;
  backOn: string;
  /** The same as day numbers: closed on from ≤ day < to. */
  from: number;
  to: number;
};

/**
 * What closures do to an order, for the why ("+21 days: Chinese New Year (Jan 30–Feb 20)"): `days` earlier its
 * order-by is than the lead time alone, or, `late` (that day has passed, so it's placed today), days later it lands.
 */
export type ClosureDelay = { days: number; late: boolean; hit: Closure[] };

const DAY_MS = 86_400_000;
const dayOf = (iso: string) => Math.round(Date.parse(`${String(iso).slice(0, 10)}T00:00:00Z`) / DAY_MS);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const short = (iso: string, year: boolean) => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}${year ? `, ${y}` : ""}`;
};

/** A new vendor's country from its time zone, else CN for CNY prices ("" when neither says; migration 032 likewise). */
export function countryGuess(v: { timezone?: string | null; currency?: string | null }): string {
  const tz = v.timezone ?? "";
  if (["Asia/Shanghai", "Asia/Chongqing", "Asia/Chungking", "Asia/Harbin", "Asia/Urumqi", "Asia/Kashgar", "PRC"].includes(tz)) return "CN";
  if (tz === "Asia/Hong_Kong") return "HK";
  if (tz === "Asia/Taipei") return "TW";
  return v.currency === "CNY" ? "CN" : "";
}

/** "Jan 30–Feb 20" (the year only when it ends in another one: "Dec 28, 2026–Jan 4, 2027"). */
export function closureDates(c: Pick<ClosureRow, "closed_from" | "back_on">): string {
  const other = String(c.closed_from).slice(0, 4) !== String(c.back_on).slice(0, 4);
  return `${short(c.closed_from, other)}–${short(c.back_on, other)}`;
}

/** Days closed: back on − closed from (0 = it doesn't close). */
export const closureDays = (c: Pick<ClosureRow, "closed_from" | "back_on">) => Math.max(0, dayOf(c.back_on) - dayOf(c.closed_from));

/** The rows that apply to a vendor: its country's (or its own dates for one) and its own, by date. */
export function closuresOf(vendor: Pick<Vendor, "id"> & { country?: string | null }, rows: readonly ClosureRow[]): ClosureRow[] {
  const own = rows.filter((c) => c.vendor_id === vendor.id);
  const replaced = new Set(own.map((c) => c.replaces_id).filter(Boolean));
  const country = vendor.country ?? "";
  const theirs = country ? rows.filter((c) => !c.vendor_id && c.country === country && !replaced.has(c.id)) : [];
  return [...theirs, ...own].sort((a, b) => String(a.closed_from).localeCompare(String(b.closed_from)));
}

/** Each vendor's closures as day numbers from `origin` (the plan's today), only the ones with days closed. */
export function closureIndex(
  vendors: readonly (Pick<Vendor, "id"> & { country?: string | null })[],
  rows: readonly ClosureRow[] | undefined,
  origin = 0,
): Map<string, Closure[]> {
  const out = new Map<string, Closure[]>();
  if (!rows?.length) return out;
  // A vendor's own dates for a country's closure go by that closure's name (renamed since, too).
  const labelOf = new Map(rows.map((c) => [c.id, c.label]));
  for (const v of vendors) {
    const list = closuresOf(v, rows)
      .filter((c) => closureDays(c) > 0)
      .map((c) => ({
        id: c.id,
        label: (c.replaces_id && labelOf.get(c.replaces_id)) || c.label,
        closedFrom: String(c.closed_from).slice(0, 10),
        backOn: String(c.back_on).slice(0, 10),
        from: dayOf(c.closed_from) - origin,
        to: dayOf(c.back_on) - origin,
      }));
    if (list.length) out.set(v.id, list);
  }
  return out;
}

/** Closures merged where they overlap, by date (a vendor's own and its country's can). */
function merged(cs: readonly Closure[]): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  for (const c of [...cs].sort((a, b) => a.from - b.from)) {
    const last = out.at(-1);
    if (last && c.from <= last.to) last.to = Math.max(last.to, c.to);
    else out.push({ from: c.from, to: c.to });
  }
  return out;
}

/** The day an order placed on `start` lands: `lead` days the vendor works later. */
export function landsOn(cs: readonly Closure[] | undefined, start: number, lead: number): number {
  if (!cs?.length) return start + lead;
  let cur = start;
  let left = lead;
  for (const c of merged(cs)) {
    if (c.to <= cur) continue;
    const open = Math.max(cur, c.from) - cur;
    if (open >= left) return cur + left;
    left -= open;
    cur = c.to;
  }
  return cur + left;
}

/** The latest day to place an order that lands by `need` (it may land a little before, when a closure is in the way). */
export function startBy(cs: readonly Closure[] | undefined, need: number, lead: number): number {
  if (!cs?.length) return need - lead;
  let cur = need;
  let left = lead;
  for (const c of merged(cs).reverse()) {
    if (c.from >= cur) continue;
    const open = cur - Math.min(cur, c.to);
    if (open >= left) return cur - left;
    left -= open;
    cur = c.from;
  }
  return cur - left;
}

/**
 * What closures do to an order needed on day `need` with `lead`: how much earlier it's ordered (or, when that day is
 * before `today`, how much later it lands), and the closures in the way; null when they change nothing.
 */
export function delayFor(cs: readonly Closure[] | undefined, need: number, lead: number, today = 0): ClosureDelay | null {
  if (!cs?.length) return null;
  const by = startBy(cs, need, lead);
  const late = by < today;
  const start = late ? today : by;
  const land = landsOn(cs, start, lead);
  const days = late ? land - start - lead : need - lead - by;
  if (days <= 0) return null;
  const end = Math.max(land, need);
  return { days, late, hit: cs.filter((c) => c.from < end && c.to > start) };
}

/** "+21 days: Chinese New Year (Jan 30–Feb 20)". */
export function delayText(d: ClosureDelay): string {
  const names = d.hit.map((c) => `${c.label} (${closureDates({ closed_from: c.closedFrom, back_on: c.backOn })})`);
  return `+${d.days} day${d.days === 1 ? "" : "s"}: ${names.join(", ")}`;
}
